import { Formiz, useForm } from "@formiz/core";
import { isMaxLength, isMinLength, isRequired } from "@formiz/validations";
import { NotificationType, UserVoice, useTranslation } from "@next-platform/client-common-core";
import { Button, ButtonVariant, Input, Text, TextArea, TextVariants } from "@next-platform/client-common-spicy-ui";
import type { TFunction } from "i18next";
import { useCallback, useMemo, useState } from "react";
import type { BlockerFunction } from "react-router-dom";

import { GenerateSampleSection } from "@/components";
import DiscardChangesModalV2 from "@/components/common/DiscardChangesModalV2";
import ReactRouterPrompt from "@/components/common/ReactRouterPrompt";
import { Col, FormField, Row } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";
import useToast from "@/hooks/useToast";
import { DESCRIPTION_MAX_LENGTH, DESCRIPTION_MIN_LENGTH, VOICE_NAME_MAX_LENGTH, VOICE_NAME_MIN_LENGTH } from "@/pages/Voice/constants";
import { isStringContainingURL } from "@/utils";

import { EditVoiceDetailProps } from "../types";

function buildNameValidationRules(t: TFunction) {
  return [
    { rule: isRequired(), message: t("voice:form.validation.name.required") },
    { rule: isMaxLength(VOICE_NAME_MAX_LENGTH), message: t("voice:form.validation.name.tooLong", { max: VOICE_NAME_MAX_LENGTH }) },
    {
      rule: (value: string) => isMinLength(VOICE_NAME_MIN_LENGTH)(value?.trim()),
      message: t("voice:form.validation.name.tooShort", { min: VOICE_NAME_MIN_LENGTH }),
    },
    {
      rule: (value: string) => !isStringContainingURL(value),
      message: t("voice:form.validation.name.noUrls"),
    },
  ];
}

function buildDescriptionValidationRules(t: TFunction) {
  return [
    { rule: isRequired(), message: t("voice:form.validation.description.required") },
    { rule: isMaxLength(DESCRIPTION_MAX_LENGTH), message: t("voice:form.validation.description.tooLong", { max: DESCRIPTION_MAX_LENGTH }) },
    {
      rule: (value: string) => isMinLength(DESCRIPTION_MIN_LENGTH)(value?.trim()),
      message: t("voice:form.validation.description.tooShort", { min: DESCRIPTION_MIN_LENGTH }),
    },
    {
      rule: (value: string) => !isStringContainingURL(value),
      message: t("voice:form.validation.description.noUrls"),
    },
  ];
}

function hasVoiceChanged(values: UserVoice, voice: UserVoice | null) {
  return (values?.name || "") !== (voice?.name || "") || (values?.description || "") !== (voice?.description || "");
}

function NavBlockerModal({ isActive, onConfirm, onCancel }: { isActive: boolean; onConfirm: () => void; onCancel: () => void }) {
  const { t } = useTranslation();
  if (!isActive) return null;
  return (
    <DiscardChangesModalV2
      heading={t("common:modal.unsavedChanges.title")}
      description={t("common:modal.unsavedChanges.message")}
      isVisible={true}
      onUserConfirmation={onConfirm}
      handleClose={onCancel}
    />
  );
}

export function EditVoiceDetail({ voice, isPlaying, isLoading, sampleText, onSpeak, onStop, onCancel, onSave }: EditVoiceDetailProps) {
  const editVoiceForm = useForm();
  const { isValid, values, isStepPristine } = editVoiceForm;
  const { t } = useTranslation();
  const { displayToast } = useToast();
  const [stateShowRequiredFormFieldsError, setStateShowRequiredFormFieldsError] = useState(false);
  const [stateShowDiscardModal, setStateShowDiscardModal] = useState(false);

  const nameValidationRules = useMemo(() => buildNameValidationRules(t), [t]);
  const descriptionValidationRules = useMemo(() => buildDescriptionValidationRules(t), [t]);

  const initialValues = useMemo(() => {
    return {
      name: (voice?.name || "").slice(0, VOICE_NAME_MAX_LENGTH),
      description: (voice?.description || "").slice(0, DESCRIPTION_MAX_LENGTH),
      preview_sample: sampleText,
    };
  }, [voice?.name, voice?.description, sampleText]);

  const hasFormChanged = useMemo(() => hasVoiceChanged(values, voice), [values, voice]);
  const isSaveButtonVisuallyDisabled = !hasFormChanged;
  const hasUnsavedChanges = !isStepPristine && hasFormChanged;

  const handleCancelClick = useCallback(() => {
    if (hasUnsavedChanges) {
      setStateShowDiscardModal(true);
    } else {
      onCancel();
    }
  }, [hasUnsavedChanges, onCancel]);

  const handleConfirmDiscard = useCallback(() => {
    setStateShowDiscardModal(false);
    onCancel();
  }, [onCancel]);

  const handleCloseDiscardModal = useCallback(() => {
    setStateShowDiscardModal(false);
  }, []);

  const handleSubmit = useCallback(() => {
    if (isSaveButtonVisuallyDisabled) {
      displayToast({ message: t("error:noChangesDetected"), type: NotificationType.info });
      return;
    }
    if (!isValid) {
      setStateShowRequiredFormFieldsError(true);
      return;
    }
    onSave({ name: values.name.trim(), description: values.description.trim() });
  }, [displayToast, isSaveButtonVisuallyDisabled, isValid, onSave, t, values.description, values.name]);

  const routerPromptCallback = useCallback(
    ({ currentLocation, nextLocation }: Parameters<BlockerFunction>[0]) => {
      if (!hasUnsavedChanges) return false;
      return currentLocation.pathname !== nextLocation.pathname;
    },
    [hasUnsavedChanges],
  );

  return (
    <>
      <ReactRouterPrompt when={routerPromptCallback}>
        {({ isActive, onConfirm, onCancel: onCancelNav }: { isActive: boolean; onConfirm: () => void; onCancel: () => void }) => (
          <NavBlockerModal isActive={isActive} onConfirm={onConfirm} onCancel={onCancelNav} />
        )}
      </ReactRouterPrompt>

      <NavBlockerModal isActive={stateShowDiscardModal} onConfirm={handleConfirmDiscard} onCancel={handleCloseDiscardModal} />

      <Formiz connect={editVoiceForm} initialValues={initialValues} onSubmit={handleSubmit}>
        <form onSubmit={editVoiceForm.submit}>
          <Col className="gap-4 mb-[39px]">
            <Text variant={TextVariants.HEADING_5_BOLD}>
              <Trans i18nKey="voice:form.edit.title" />
            </Text>
            <FormField as="custom" name="name" showError={stateShowRequiredFormFieldsError} validations={nameValidationRules}>
              <Input
                label={t("voice:form.create.field.name.label")}
                helperText={t("voice:form.create.field.name.helper")}
                placeholder={t("voice:form.edit.field.name.placeholder")}
                countLabel={`${values?.name?.length || 0}/${VOICE_NAME_MAX_LENGTH}`}
                maxLength={VOICE_NAME_MAX_LENGTH}
                showMaxLengthLabel={false}
                aria-label="voice-name"
                required
              />
            </FormField>

            <FormField as="custom" name="description" showError={stateShowRequiredFormFieldsError} validations={descriptionValidationRules}>
              <TextArea
                label={t("voice:form.create.field.description.label")}
                helperText={t("voice:form.create.field.description.helper")}
                placeholder={t("voice:form.edit.field.description.placeholder")}
                countLabel={`${values?.description?.length || 0}/${DESCRIPTION_MAX_LENGTH}`}
                rows={2}
                maxRows={4}
                maxLength={DESCRIPTION_MAX_LENGTH}
                showMaxLengthLabel={false}
                aria-label="voice-description"
                required
              />
            </FormField>
          </Col>

          <FormField as="custom" name="preview_sample">
            <GenerateSampleSection sampleText={sampleText} isPlaying={isPlaying} isLoading={isLoading} onSpeak={onSpeak} onStop={onStop} />
          </FormField>

          <Row className="gap-sm justify-end mt-[39px]">
            <Button variant={ButtonVariant.OUTLINED} onClick={handleCancelClick} aria-label="cancel">
              <Trans i18nKey="common:cancel" />
            </Button>
            <Button
              variant={ButtonVariant.CONTAINED}
              type="button"
              onClick={handleSubmit}
              aria-disabled={isSaveButtonVisuallyDisabled}
              className={isSaveButtonVisuallyDisabled ? "opacity-50 cursor-not-allowed" : ""}
              aria-label="save"
            >
              <Trans i18nKey="voice:form.edit.cta.save" />
            </Button>
          </Row>
        </form>
      </Formiz>
    </>
  );
}
